import get from 'lodash/get';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { getNormalisedPerformanceFFs } from '@atlassian/jira-common-long-task-metrics/src/common/util/collectors.tsx';
import { fireOperationalAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { scheduleAfterRender } from '@atlassian/jira-software-react-scheduler/src/index.tsx';
import { sendFailToMoveAnalytic, sendSuccessToMoveAnalytic } from '../../../../common/utils.tsx';
import { ASSIGNEE, LABEL, TEXT } from '../../../../model/filters/constants.tsx';
import type { CardId } from '../../../../model/types.tsx';
import { getBacklogCardCount } from '../../../../services/performance/backlog-data-collector/index.tsx';
import {
	issueRankInteraction,
	issueCreateInteraction,
	issueDeleteInteraction,
	flagToggleInteraction,
} from '../../../../services/utils/performance-analytics/index.tsx';
import type {
	CardCreateFailureAction,
	CardCreateSuccessAction,
	CardCreateFilteredAction,
	CardCreateFilteredV2Action,
	CardCreateMultipleRequestAction,
} from '../../../actions/cards/create/index.tsx';
import type {
	CardDeleteFailureAction,
	CardDeleteRequestAction,
	CardDeleteSuccessAction,
} from '../../../actions/cards/delete/index.tsx';
import type {
	CardsFlagWithCommentFailureAction,
	CardsFlagWithCommentRequestAction,
	CardsFlagWithCommentSuccessAction,
} from '../../../actions/cards/flag-with-comment/index.tsx';
import type {
	CardsMoveFailureAction,
	CardsMoveSuccessAction,
} from '../../../actions/cards/move/index.tsx';
import type { OpenDetailViewAction } from '../../../actions/detail-view/index.tsx';
import type { State } from '../../../reducers/types.tsx';
import { getIsCompanyManaged } from '../../../selectors/app/index.tsx';
import { getCards } from '../../../selectors/cards/index.tsx';
import { getFilters } from '../../../selectors/filters/index.tsx';
import { makeIsCardListActiveSprint } from '../../../selectors/sprints/index.tsx';

export const getIssueTrackEventPayload = (
	cardId: CardId,
	issueTypeId: number,
	createdWithRank: boolean,
) => ({
	actionSubjectId: cardId,
	objectType: 'issue',
	objectId: cardId,
	issueTypeId,
	createdWithRank,
});

export const cardCreateSuccess =
	(eventPrefix: string) => (preState: State, state: State, action: CardCreateSuccessAction) => {
		const filterValues = getFilters(state);

		// There is only ever at most 1 text filter
		const textFilter = get(filterValues, [TEXT, 'length'], 0) > 0;
		const numAssigneeFilters = get(filterValues, [ASSIGNEE, 'length'], 0);
		const numLabelFilters = get(filterValues, [LABEL, 'length'], 0);

		const cardId = action.payload.card.id;
		const issueTypeId = action.payload.card.typeId;
		const { createdWithRank } = action.payload;
		const {
			meta: { analyticsEvent },
		} = action;
		const trackEvents: Array<UIAnalyticsEvent> = [];
		const event = analyticsEvent?.clone();
		if (event != null) {
			trackEvents.push(
				event.update({
					...getIssueTrackEventPayload(cardId, issueTypeId, createdWithRank),
					appliedFilters: Object.keys(filterValues),
					card: action.payload.card,
				}),
			);
		}

		return {
			name: `${eventPrefix}.card.create.success`,
			data: {
				cardId,
				issueTypeId,
				transactionId: action.meta.optimistic.id,
				textFilter,
				numAssigneeFilters,
				numLabelFilters,
			},
			trackEvents,
		};
	};

export const cardCreateFailure =
	(eventPrefix: string) => (preState: State, state: State, action: CardCreateFailureAction) => {
		const {
			meta: { analyticsEvent },
			payload: { errorMessage, statusCode, numCards, isClientFetchError },
		} = action;
		const isCompanyManaged = getIsCompanyManaged(state);

		if (analyticsEvent) {
			for (let i = 0; i < numCards; i += 1) {
				const clonedAnalyticsEvent = analyticsEvent.clone();
				// Will be null if it's already been fired
				if (clonedAnalyticsEvent) {
					fireOperationalAnalytics(clonedAnalyticsEvent, 'issue createdFailed', {
						errorMessage,
						isClientFetchError,
						statusCode,
						isCompanyManaged,
					});
				}
			}
		}

		return {
			name: `${eventPrefix}.card.create.failure`,
			data: {
				transactionId: action.meta.optimistic.id,
				isCompanyManaged,
			},
		};
	};

export const cardCreateFiltered =
	(eventPrefix: string) => (preState: State, state: State, action: CardCreateFilteredAction) => {
		const card = action.payload;
		const filterValues = getFilters(state);

		// There is only ever at most 1 text filter
		const textFilter = get(filterValues, [TEXT, 'length'], 0) > 0;
		const numAssigneeFilters = get(filterValues, [ASSIGNEE, 'length'], 0);
		const numLabelFilters = get(filterValues, [LABEL, 'length'], 0);

		return {
			name: `${eventPrefix}.filter.flag.new-card-filtered.shown`,
			data: {
				cardId: card.id,
				textFilter,
				numAssigneeFilters,
				numLabelFilters,
			},
		};
	};

export const cardCreateFilteredV2 =
	(eventPrefix: string) => (preState: State, state: State, action: CardCreateFilteredV2Action) => {
		const cardIds = action.payload;
		const filterValues = getFilters(state);

		// There is only ever at most 1 text filter
		const textFilter = get(filterValues, [TEXT, 'length'], 0) > 0;
		const numAssigneeFilters = get(filterValues, [ASSIGNEE, 'length'], 0);
		const numLabelFilters = get(filterValues, [LABEL, 'length'], 0);

		return {
			name: `${eventPrefix}.filter.flag.new-card-filtered.shown`,
			data: {
				cardIds,
				textFilter,
				numAssigneeFilters,
				numLabelFilters,
			},
		};
	};

export const cardDeleteSuccess =
	(eventPrefix: string) => (preState: State, state: State, action: CardDeleteSuccessAction) => {
		const concurrentId = action.meta.optimistic.id;
		issueDeleteInteraction(concurrentId).stop({
			customData: { ...getBacklogCardCount(state) },
		});

		return {
			name: `${eventPrefix}.card.delete.success`,
			data: {
				cardId: action.payload.cardId,
			},
		};
	};

export const cardDeleteFailure =
	(eventPrefix: string) => (preState: State, state: State, action: CardDeleteFailureAction) => {
		const concurrentId = action.meta.optimistic.id;
		issueDeleteInteraction(concurrentId).cancel();

		return {
			name: `${eventPrefix}.card.delete.failure`,
			data: {
				cardId: action.payload.cardId,
			},
		};
	};

export const cardsFlagSuccess =
	(eventPrefix: string) =>
	(preState: State, state: State, action: CardsFlagWithCommentSuccessAction) => {
		const concurrentId = action.meta.optimistic.id;
		const isCompanyManaged = getIsCompanyManaged(state);
		flagToggleInteraction(concurrentId).stop({
			customData: { ...getBacklogCardCount(state), isCompanyManaged },
		});
		const { cards } = action.payload;
		const flagDirection = getCards(state)[String(cards[0])].isFlagged ? 'added' : 'removed';
		return {
			name: `${eventPrefix}.cards.flag.${flagDirection}`,
			data: {
				numCards: cards.length,
			},
		};
	};

export const cardsFlagFailure =
	(eventPrefix: string) =>
	(preState: State, state: State, action: CardsFlagWithCommentFailureAction) => {
		const concurrentId = action.meta.optimistic.id;
		flagToggleInteraction(concurrentId).cancel();
		const { cards } = action.payload;
		return {
			name: `${eventPrefix}.cards.flag.failure`,
			data: {
				numCards: cards.length,
			},
		};
	};

export const cardOpenDetailView =
	(eventPrefix: string) => (preState: State, state: State, action: OpenDetailViewAction) => ({
		name: `${eventPrefix}.card.open-detail-view`,
		data: {
			cardId: action.payload.cardId,
		},
	});

export const cardCreateMultipleRequestPerformance =
	() => (preState: State, state: State, action: CardCreateMultipleRequestAction) => {
		const concurrentIds = action.payload.tempIds;
		concurrentIds.forEach((concurrentId) => {
			issueCreateInteraction(String(concurrentId)).start();
		});
		scheduleAfterRender(() => {
			concurrentIds.forEach((concurrentId) => {
				issueCreateInteraction(String(concurrentId)).mark('feedback');
			});
		});
		return null;
	};

export const cardCreateSuccessPerformance =
	() => (preState: State, state: State, action: CardCreateSuccessAction) => {
		const {
			payload: { temporaryCardId, listId, createdWithBulk },
		} = action;
		const concurrentId = String(temporaryCardId);
		const isCompanyManaged = getIsCompanyManaged(state);
		// listId potential values: 'BOARD', 'BACKLOG' or sprintId e.g. '155'
		const cardListType = Number.isInteger(Number(listId)) ? 'SPRINT' : listId;
		issueCreateInteraction(concurrentId).stop({
			customData: {
				...getNormalisedPerformanceFFs(),
				...getBacklogCardCount(state),
				cardListType,
				createdWithBulk,
				isCompanyManaged,
			},
		});
		return null;
	};

export const cardRankRequestPerformance = () => () => {
	issueRankInteraction.start();
	scheduleAfterRender(() => {
		issueRankInteraction.mark('feedback');
	});
	return null;
};

export const cardDeleteRequestPerformance =
	() => (preState: State, state: State, action: CardDeleteRequestAction) => {
		const concurrentId = action.meta.optimistic.id;
		issueDeleteInteraction(concurrentId).start();
		scheduleAfterRender(() => {
			issueDeleteInteraction(concurrentId).mark('feedback');
		});
		return null;
	};

export const cardsMoveSuccess =
	() => (preState: State, state: State, action: CardsMoveSuccessAction) => {
		const {
			meta: { analyticsEvent },
			payload: { cardIds, cardListId },
		} = action;

		if (!analyticsEvent) {
			return null;
		}

		const isCompanyManaged = getIsCompanyManaged(state);
		const isActiveSprint = makeIsCardListActiveSprint(state);
		cardIds.forEach((id) =>
			sendSuccessToMoveAnalytic(
				id,
				// @ts-expect-error Argument of type 'UIAnalyticsEvent | null' is not assignable to parameter of type 'UIAnalyticsEvent'.
				analyticsEvent.clone(),
				cardListId,
				isActiveSprint,
			),
		);
		issueRankInteraction.stop({
			customData: {
				...getNormalisedPerformanceFFs(),
				...getBacklogCardCount(state),
				isCompanyManaged,
			},
		});

		return null;
	};
export const cardsMoveFailure =
	() => (preState: State, state: State, action: CardsMoveFailureAction) => {
		const {
			meta: { analyticsEvent },
			payload: { error, cardIds, cardListId },
		} = action;

		if (!analyticsEvent) {
			return null;
		}

		const isActiveSprint = makeIsCardListActiveSprint(state);
		sendFailToMoveAnalytic(analyticsEvent, cardIds, error, cardListId, isActiveSprint);
		issueRankInteraction.cancel();
		return null;
	};

export const cardsFlagRequestPerformance =
	() => (preState: State, state: State, action: CardsFlagWithCommentRequestAction) => {
		const concurrentId = action.meta.optimistic.id;
		flagToggleInteraction(concurrentId).start();
		scheduleAfterRender(() => {
			flagToggleInteraction(concurrentId).mark('feedback');
		});
		return null;
	};

export const cardsParentSetSuccess = (eventPrefix: string) => () => ({
	name: `${eventPrefix}.cards.parent-set.success`,
});

export const cardsParentSetFailure = (eventPrefix: string) => () => ({
	name: `${eventPrefix}.cards.parent-set.failure`,
});
